import React, { useState } from 'react';
import { Plus, Folder, FolderOpen, ChevronRight, Edit2, Check, X, Trash2 } from 'lucide-react';
import { type Project, updateProject, softDeleteProject } from '../../../../lib/apis_vibe';

interface ProjectSidebarProps {
  projects: Project[];
  selectedProjectId: number | null;
  onSelectProject: (id: number) => void;
  onCreateProject: () => void;
  onProjectUpdated?: () => void;
  onProjectDeleted?: () => void;
}

const ProjectSidebar: React.FC<ProjectSidebarProps> = ({
  projects,
  selectedProjectId,
  onSelectProject,
  onCreateProject,
  onProjectUpdated,
  onProjectDeleted
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const startEditing = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation(); // 防止触发选择
    setEditingId(project.id);
    setEditName(project.name);
  };

  const cancelEditing = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingId(null);
    setEditName('');
  };

  const handleRename = async (e: React.MouseEvent | React.KeyboardEvent, id: number) => {
    e.stopPropagation();
    if (!editName.trim()) return;

    try {
      await updateProject(id, { name: editName });
      setEditingId(null);
      if (onProjectUpdated) onProjectUpdated();
    } catch (err) {
      alert("重命名失败");
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    setIsDeleting(true);
    try {
      await softDeleteProject(projectId);
      setDeleteConfirmId(null);
      if (onProjectDeleted) onProjectDeleted();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "删除失败";
      alert(msg);
      setDeleteConfirmId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <aside className="w-64 bg-[#09090b] border-r border-white/10 flex flex-col h-full">
      {/* 头部 */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h2 className="text-gray-400 text-xs font-bold uppercase tracking-wider">我的项目</h2>
        <button 
          onClick={onCreateProject}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
          title="新建项目"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* 列表 */}
      <div className="flex-1 overflow-y-auto py-2 custom-scrollbar">
        {projects.length === 0 ? (
          <div className="text-center mt-10 px-4">
            <p className="text-sm text-gray-500 mb-3">还没有项目</p>
            <button 
              onClick={onCreateProject}
              className="text-xs text-purple-400 hover:text-purple-300 underline"
            >
              立即创建一个
            </button>
          </div>
        ) : (
          <ul className="space-y-0.5 px-2">
            {projects.map((project) => {
              const isActive = project.id === selectedProjectId;
              const isEditing = project.id === editingId;

              return (
                <li key={project.id}>
                  {isEditing ? (
                    // 编辑模式
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-white/5 rounded-lg border border-purple-500/50">
                      <FolderOpen size={18} className="text-purple-500 shrink-0" />
                      <input 
                        type="text" 
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 bg-transparent text-sm text-white outline-none min-w-0"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(e, project.id);
                          if (e.key === 'Escape') cancelEditing();
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex gap-1 shrink-0">
                        <button 
                          onClick={(e) => handleRename(e, project.id)}
                          className="p-1 text-green-400 hover:bg-green-400/20 rounded"
                        >
                          <Check size={12} />
                        </button>
                        <button 
                          onClick={cancelEditing}
                          className="p-1 text-red-400 hover:bg-red-400/20 rounded"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative group">
                      <button
                        onClick={() => onSelectProject(project.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all pr-16 ${
                          isActive 
                            ? 'bg-purple-500/10 text-purple-300' 
                            : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                        }`}
                      >
                        {isActive ? (
                          <FolderOpen size={18} className="text-purple-500 shrink-0" />
                        ) : (
                          <Folder size={18} className="group-hover:text-gray-300 shrink-0" />
                        )}
                        
                        <span className="truncate flex-1 text-left">{project.name}</span>
                        
                        {isActive && <ChevronRight size={14} className="opacity-50" />}
                      </button>

                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => startEditing(e, project)}
                          className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded"
                          title="重命名"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(project.id);
                          }}
                          className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded"
                          title="删除项目"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {deleteConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#18181b] border border-red-500/50 rounded-xl p-6 w-full max-w-sm shadow-2xl shadow-red-500/20 animate-in fade-in zoom-in duration-200">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="text-5xl mb-3">😱🗑️</div>
              <h3 className="text-xl font-bold text-red-500 mb-2">确认删除项目？</h3>
              <p className="text-sm text-gray-400">
                项目下的所有文件也将被删除，此操作<span className="text-red-500 font-bold">不可恢复</span>！
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => handleDeleteProject(deleteConfirmId)}
                disabled={isDeleting}
                className="w-full py-3 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2 border border-red-400"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    删除中...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    确认删除
                  </>
                )}
              </button>
              
              <button 
                onClick={() => setDeleteConfirmId(null)}
                disabled={isDeleting}
                className="w-full py-3 bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition-all"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default ProjectSidebar;
